/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Box,
  Grid,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import React from 'react';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import {
  CustomIconButton,
  CustomTooltip,
  SearchHighlight,
  SystemScope,
  ToggleMenu,
} from '@opengeoweb/shared';
import {
  Info,
  SharedPresetsMyUser,
  SharedPresetsOtherUsers,
  WorkspacePresetsMyIcon,
  WorkspacePresetsPluginIcon,
  WorkspacePresetsSystemIcon,
} from '@opengeoweb/theme';
import type { WorkspacePresetListItem } from '../../store/workspace/types';
import { WorkspacePresetAction } from '../../store/workspace/types';
import { useWorkspaceTranslation } from '../../utils/i18n';
import { getWorkspaceRouteUrl } from '../../utils/routes';

export const WORKSPACE_SELECT_LIST_ROW_HEIGHT = 40;

export const getPresetIcon = (
  preset: WorkspacePresetListItem,
  currentUsername?: string,
): React.ReactElement | null => {
  if (preset.scope === 'system') {
    return (
      <WorkspacePresetsSystemIcon
        data-testid="systemPresetIcon"
        sx={{ color: 'geowebColors.typographyAndIcons.systemScopeIcon' }}
      />
    );
  }

  if (preset.scope === 'plugin') {
    return (
      <WorkspacePresetsPluginIcon
        data-testid="WorkspacePresetsPluginIcon"
        sx={{ color: 'geowebColors.typographyAndIcons.icon' }}
      />
    );
  }

  if (preset.is_shared) {
    if (preset.username && preset.username === currentUsername) {
      return (
        <SharedPresetsMyUser
          data-testid="sharedByMeIcon"
          sx={{
            color: 'geowebColors.typographyAndIcons.userScopeIcon',
          }}
        />
      );
    }

    return (
      <SharedPresetsOtherUsers
        data-testid="sharedByOtherIcon"
        sx={{ color: 'geowebColors.typographyAndIcons.sharedByOthersIcon' }}
      />
    );
  }

  if (preset.username && preset.username === currentUsername) {
    return (
      <WorkspacePresetsMyIcon
        data-testid="myPrivatePresetIcon"
        sx={{ color: 'geowebColors.typographyAndIcons.userScopeIcon' }}
      />
    );
  }

  return null;
};
interface WorkspaceSelectListRowProps {
  workspacePreset: WorkspacePresetListItem;
  onClickWorkspacePreset: (id: string) => void;
  onClickWorkspacePresetOption: (
    presetId: string,
    workspaceAction: WorkspacePresetAction,
    workspacePreset: WorkspacePresetListItem,
  ) => void;
  onOpenPresetInfo?: (workspacePreset: WorkspacePresetListItem) => void;
  infoOpenPresetId?: string;
  isOptionsMenuOpen?: boolean;
  isEditable?: boolean;
  isAdmin?: boolean;
  searchQuery?: string;
  isSelected?: boolean;
  testIdPrefix?: string; // Specifies if used for workspace or viewpreset, added to testids
  onSelectVersion?: (presetId: string, version: string) => void;
}

const WorkspaceSelectListRow: React.FC<WorkspaceSelectListRowProps> = ({
  workspacePreset,
  isSelected,
  onClickWorkspacePreset,
  onClickWorkspacePresetOption,
  onOpenPresetInfo,
  infoOpenPresetId,
  isOptionsMenuOpen = false,
  isEditable = false,
  isAdmin = false,
  searchQuery = '',
  testIdPrefix = 'workspace',
  onSelectVersion,
}) => {
  const { auth } = useAuthenticationContext();
  const { t } = useWorkspaceTranslation();

  const onClick = (action: WorkspacePresetAction): void =>
    onClickWorkspacePresetOption(workspacePreset.id, action, workspacePreset);

  const isInfoOpen = infoOpenPresetId === workspacePreset.id;

  const onOpenInfoClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    event.stopPropagation();
    onOpenPresetInfo?.(workspacePreset);
  };

  const canDelete =
    ((workspacePreset.scope === SystemScope.user &&
      auth?.username === workspacePreset.username) ||
      isAdmin) &&
    Boolean(workspacePreset.id);

  const menuItems: {
    text: string;
    action: () => void;
    isDisabled?: boolean;
  }[] = [
    {
      text: t('workspace-duplicate'),
      action: () => onClick(WorkspacePresetAction.DUPLICATE),
    },
    {
      text: t('workspace-open-tab'),
      action: () => onClick(WorkspacePresetAction.OPEN_TAB),
    },
    {
      text:
        testIdPrefix === 'viewpreset'
          ? t('workspace-copy-viewpreset-to-clipboard')
          : t('workspace-copy-to-clipboard'),
      action: () => onClick(WorkspacePresetAction.COPY_TO_CLIPBOARD),
    },
  ];

  // Show "Edit" if it's your own user-scoped preset OR you're an admin
  if (
    (workspacePreset.scope === SystemScope.user &&
      auth?.username === workspacePreset.username) ||
    isAdmin
  ) {
    menuItems.push({
      text: t('workspace-edit'),
      action: () => onClick(WorkspacePresetAction.EDIT),
    });
  }

  // Show "Share/Unshare" only if it's your own user-scoped preset
  if (
    workspacePreset.scope === SystemScope.user &&
    auth?.username === workspacePreset.username
  ) {
    menuItems.push({
      text: workspacePreset.is_shared
        ? t('workspace-stop-share')
        : t('workspace-share'),
      action: () => onClick(WorkspacePresetAction.SHARE),
      isDisabled: false,
    });
  }

  const description = workspacePreset?.description ?? '';

  const tooltipContent = (
    <Box>
      <Typography fontWeight={600} variant="body2">
        {workspacePreset.title}
      </Typography>
      {!!description && <Typography variant="body2">{description}</Typography>}
    </Box>
  );
  if (canDelete) {
    menuItems.push({
      text: t('workspace-delete'),
      action: () =>
        onClick(
          isAdmin
            ? WorkspacePresetAction.DELETE_SYSTEM_PRESET
            : WorkspacePresetAction.DELETE,
        ),
    });
  }

  const versions =
    workspacePreset.scope === 'plugin'
      ? (workspacePreset.origin?.versions ?? [])
      : [];

  return (
    <ListItem
      disablePadding
      sx={{
        mb: 0.5,
        ml: 0.5,
        borderRadius: 1.5,
        backgroundColor: 'geowebColors.cards.cardContainer',
        boxShadow:
          '0px 4px 4px rgba(15, 23, 42, 0.2), 2px 2px 6px rgba(15, 23, 42, 0.12)',
        transition: 'box-shadow 0.15s ease-out, transform 0.15s ease-out',
        ' .MuiListItemText-primary': {
          fontSize: '16px',
          fontWeight: 500,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        },
        ' .MuiListItemText-secondary': {
          fontSize: '10px',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          lineHeight: '1.2',
          letterSpacing: '0.18px',
          whiteSpace: 'nowrap',
          color: 'geowebColors.typographyAndIcons.text',
        },
      }}
    >
      <ListItemButton
        aria-label={testIdPrefix}
        data-testid={`${testIdPrefix}-selectListRow`}
        onClick={(event: React.MouseEvent): void => {
          if (event.ctrlKey || event.metaKey) {
            const url = getWorkspaceRouteUrl(workspacePreset.id);
            window.open(url, '_blank');
          } else {
            onClickWorkspacePreset(workspacePreset.id);
          }
        }}
        role="listitem"
        selected={isSelected}
        sx={{
          pl: 2,
          pr: 1,
          py: 1,
          height: `${WORKSPACE_SELECT_LIST_ROW_HEIGHT}px`,
        }}
      >
        <ListItemIcon>
          {getPresetIcon(workspacePreset, auth?.username)}
        </ListItemIcon>

        <ListItemText
          primary={
            <CustomTooltip
              enterDelay={300}
              placement="bottom"
              title={tooltipContent}
            >
              <Box
                component="span"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                <SearchHighlight
                  search={searchQuery}
                  text={workspacePreset?.title}
                />
              </Box>
            </CustomTooltip>
          }
          secondary={
            <CustomTooltip
              enterDelay={300}
              placement="bottom"
              title={tooltipContent}
            >
              <Box
                component="span"
                sx={{
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                <SearchHighlight search={searchQuery} text={description} />
              </Box>
            </CustomTooltip>
          }
          sx={{ margin: 0 }}
        />
        {!!isEditable && (
          <Grid sx={{ minWidth: 72, marginLeft: 2 }}>
            <Grid
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.5,
              }}
            >
              {!!workspacePreset.id && (
                <CustomIconButton
                  data-testid={`${testIdPrefix}-listInfoButton`}
                  isSelected={isInfoOpen}
                  onClick={onOpenInfoClick}
                  sx={{
                    color: isInfoOpen
                      ? 'primary.main'
                      : 'geowebColors.typographyAndIcons.text',
                    backgroundColor: 'transparent',
                    '&.Mui-selected': {
                      backgroundColor: 'transparent',
                      color: 'primary.main',
                    },
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                  tooltipTitle={t('workspace-info-tooltip')}
                  variant="flat"
                >
                  <Info fontSize="small" />
                </CustomIconButton>
              )}
              {workspacePreset.scope !== 'plugin' && (
                <ToggleMenu
                  buttonTestId={`${testIdPrefix}-listOptionsButton`}
                  isDefaultOpen={isOptionsMenuOpen}
                  menuItems={menuItems}
                  menuPosition="right"
                  menuTitle={t('workspace-options')}
                />
              )}
              {!!versions.length && versions.length > 1 && (
                <ToggleMenu
                  buttonTestId={`${testIdPrefix}-listOptionsButton`}
                  isDefaultOpen={isOptionsMenuOpen}
                  menuItems={versions.map((version) => ({
                    text: `Version ${version}`,
                    action: () =>
                      onSelectVersion?.(workspacePreset.id, version),
                  }))}
                  menuPosition="right"
                  menuTitle={t('workspace-versions')}
                />
              )}
            </Grid>
          </Grid>
        )}
      </ListItemButton>
    </ListItem>
  );
};

export default WorkspaceSelectListRow;
